/**
 * SessionCard — one row in the Training Sessions list.
 *
 * Status dot + skill name + iteration count + relative timestamps.
 * The status colour follows the standard semantic palette
 * (success/warning/error/muted) — completed = green, active = yellow,
 * abandoned = red, planned = muted.
 */
import type { TrainingSessionSummary } from '@chaos/types/learning';
import { Link } from '@tanstack/react-router';

import { cn } from '../../lib/utils';
import { fmtRelative, SESSION_STATUS_LABEL, sessionStatusTone, TONE_DOT_CLASS } from './formatters';

interface SessionCardProps {
  session: TrainingSessionSummary;
}

export function SessionCard({ session }: SessionCardProps) {
  const tone = sessionStatusTone(session.status);
  const label = SESSION_STATUS_LABEL[session.status] ?? session.status;
  return (
    <div className="group/session relative flex items-center justify-between gap-3 rounded border border-border bg-background px-3 py-2.5 transition-colors hover:bg-muted/40">
      <Link
        to="/university/training/$sessionId"
        params={{ sessionId: session.id }}
        className="absolute inset-0 z-0 rounded"
        aria-label={`Open training session ${session.skillName ?? session.id}`}
      />
      <div className="relative z-10 flex min-w-0 flex-col">
        <div className="flex items-center gap-1.5">
          <span
            aria-hidden
            className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT_CLASS[tone])}
            title={label}
          />
          <span className="truncate text-[13px] font-semibold text-foreground">
            {session.skillName ?? 'Untitled session'}
          </span>
          {session.skillKey ? (
            <span className="truncate font-mono text-[11px] text-muted-foreground">
              {session.skillKey}
            </span>
          ) : null}
        </div>
        <span className="mt-0.5 truncate text-[11px] text-muted-foreground">
          {label} · {session.iterationCount} iterations · started {fmtRelative(session.startedAt)}
          {session.endedAt ? ` · ended ${fmtRelative(session.endedAt)}` : ''}
        </span>
      </div>
      {session.markedAsExample === 'positive' ? (
        <span
          className="relative z-10 inline-flex items-center gap-1 rounded border border-border bg-muted/40 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground"
          title="Approved as a positive example"
        >
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-success" />
          Example
        </span>
      ) : null}
    </div>
  );
}
